import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import getWorkcellColor from "../utils/getWorkcellColor";
import { fetchDailyHoursSummary } from "../api";
import { useNavigate } from 'react-router-dom';
import { formatHoursMinutes } from "../utils/time";
import { useTranslation } from 'react-i18next';
import { useCustomerCurrency, useCustomerLocale } from "../context/CustomerSettingsContext";
import { diffLocalDateTimeHours, formatLocalTime } from "../utils/localDateTime";

const getActivityColor = (percentage) => {
    // Below 50% is pure red
    if (percentage <= 50) {
        return 'rgb(255, 0, 0)';
    }
    // Above 95% is pure green
    if (percentage >= 95) {
        return 'rgb(0, 255, 0)';
    }

    // Two-phase transition: red -> orange -> green
    const factor = (percentage - 50) / 45; // 0 at 50%, 1 at 95%

    if (factor < 0.5) {
        // First half: red to orange (255,0,0) -> (255,165,0)
        const orangeFactor = factor * 2; // rescale 0-0.5 to 0-1
        return `rgb(255, ${Math.round(165 * orangeFactor)}, 0)`;
    } else {
        // Second half: orange to green (255,165,0) -> (0,255,0)
        const greenFactor = (factor - 0.5) * 2; // rescale 0.5-1 to 0-1
        return `rgb(${Math.round(255 * (1 - greenFactor))}, ${Math.round(165 + (255 - 165) * greenFactor)}, 0)`;
    }
};

const DailyHoursSummaryTable = ({ date, onLoadingChange }) => {
    const theme = useTheme();
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const locale = useCustomerLocale();
    const { formatCurrency } = useCustomerCurrency();

    const formatTime = (isoString) => {
        if (!isoString) return t('common.na');
        return formatLocalTime(isoString) || t('common.na');
    };

    const formatPercent = (value) => {
        const num = Number(value);
        if (!Number.isFinite(num)) return t('common.na');
        return new Intl.NumberFormat(locale, {
            style: 'percent',
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
        }).format(num / 100);
    };

    useEffect(() => {
        if (!date) {
            setData([]);
            setIsLoading(false);
            onLoadingChange?.(false);
            return;
        }
        let cancelled = false;
        setIsLoading(true);
        onLoadingChange?.(true);
        fetchDailyHoursSummary(date)
            .then(apiData => {
                if (cancelled) return;
                setData(apiData || []);
            })
            .catch(error => {
                if (cancelled) return;
                console.error('Error fetching summary data:', error);
                setData([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
                onLoadingChange?.(false);
            });
        return () => { cancelled = true; };
    }, [date]);

    const allWorkcellIds = data && data.length
        ? [...new Set(data.map(row => row.workcell_id))].sort((a, b) => a - b)
        : [];

    if (isLoading) return <div>{t('common.loading')}</div>;
    if (!data || data.length === 0) {
        return <div>{t('common.noData')}</div>;
    }

    const totalActiveHours = data.reduce((sum, row) => sum + (row.active_hours || 0), 0);
    const totalInactiveHours = data.reduce((sum, row) => sum + (row.inactive_hours || 0), 0);
    const totalPossibleHours = data.reduce(
        (sum, row) =>
            sum +
            ((row.end_time && row.start_time)
                ? diffLocalDateTimeHours(row.start_time, row.end_time)
                : 0),
        0
    );
    const totalInternalTurnover = data.reduce((sum, row) => sum + (row.internal_turnover || 0), 0);

    return (
        <Table stickyHeader>
            <TableHead>
                <TableRow>
                    <TableCell>{t('daily.hours.table.workcell')}</TableCell>
                    <TableCell align="right">{t('daily.hours.table.startTime')}</TableCell>
                    <TableCell align="right">{t('daily.hours.table.endTime')}</TableCell>
                    <TableCell align="right">{t('daily.hours.table.activeHours')}</TableCell>
                    <TableCell align="right">{t('daily.hours.table.inactiveHours')}</TableCell>
                    <TableCell align="right">{t('daily.hours.table.internalTurnover')}</TableCell>
                    <TableCell align="right">{t('daily.hours.table.activityPct')}</TableCell>
                    <TableCell sx={{ width: '10rem' }} />
                </TableRow>
            </TableHead>
            <TableBody>
                {data.map((row) => (
                    <TableRow key={row.workcell_id}>
                        <TableCell
                            component="th"
                            scope="row"
                            sx={{
                                color: getWorkcellColor(row.workcell_id, theme.palette.chart.colors, allWorkcellIds),
                                fontWeight: 'bold'
                            }}
                        >
                            {row.workcell_name}
                        </TableCell>
                        <TableCell align="right">{formatTime(row.start_time)}</TableCell>
                        <TableCell align="right">{formatTime(row.end_time)}</TableCell>
                        <TableCell align="right">{formatHoursMinutes(row.active_hours)}</TableCell>
                        <TableCell align="right">{formatHoursMinutes(row.inactive_hours)}</TableCell>
                        <TableCell align="right">{formatCurrency(row.internal_turnover || 0)}</TableCell>
                        <TableCell
                            align="right"
                            sx={{
                                position: 'relative',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    right: 0,
                                    top: 0,
                                    width: '8px',
                                    height: '100%',
                                    backgroundColor: getActivityColor(row.activity_percentage),
                                    opacity: 0.8
                                }
                            }}
                        >
                            {formatPercent(row.activity_percentage)}
                        </TableCell>
                        <TableCell align="right">
                            <Button
                            variant="contained"
                            size="small"
                            onClick={() => navigate(`/chart/workcell/${row.workcell_id}/daily/${date}`)}
                            sx={{ whiteSpace: 'nowrap' }}
                            >
                            {t('common.viewDetails')}
                            </Button>
                        </TableCell>
                    </TableRow>
                ))}
                {/* Totals row */}
                <TableRow sx={{ fontWeight: 'bold', backgroundColor: theme.palette.action.hover }}>
                    <TableCell component="th" scope="row">{t('common.total')}</TableCell>
                    <TableCell colSpan={2} align="center">{formatHoursMinutes(totalPossibleHours)}</TableCell>
                    <TableCell align="right">{formatHoursMinutes(totalActiveHours)}</TableCell>
                    <TableCell align="right">{formatHoursMinutes(totalInactiveHours)}</TableCell>
                    <TableCell align="right">{formatCurrency(totalInternalTurnover)}</TableCell>
                    <TableCell />
                    <TableCell sx={{ width: '10rem' }} />
                </TableRow>
            </TableBody>
        </Table>
    );
};

export default DailyHoursSummaryTable;
